// ---------------------------------------------------------------------------
// Block Definition: tile_row
//
// Row (or grid) of metric tiles.  Each tile has a big value (extracted,
// formula-computed, or derived from source records) and a small label.
//
// Extraction phases:
//   - structured: extracts scalar values from structured JSON pins (formula inputs)
//   - deterministic: computes derived values from parsed source records (derive)
//   - llm: fills extract keys from text sources
// A tile_row can mix all three phases depending on tile configuration.
// ---------------------------------------------------------------------------

const EVIDENCE_DESC = "Verbatim sentence(s) from the SOURCE that justify this value — quote exactly, do not paraphrase. If combining separate spans, join them with ' … '. null if not directly stated.";

function nullable(type, description, extra = {}) {
  const t = Array.isArray(type) ? type : [type, "null"];
  return { type: t, ...(description ? { description } : {}), ...extra };
}

function readTile(t) {
  // Callout tiles carry their content in `tile.text`; all other tiles use `tile.value`.
  const isCallout = t?.style === "callout";
  const v = isCallout ? (t?.text || {}) : (t?.value || {});
  const l = typeof t?.label === "object" ? (t.label || {}) : {};
  return {
    extract: v.extract,
    formula: v.formula,
    format: v.format,
    suffix: v.suffix,
    prompt: v.prompt,
    cite: v.cite,
    labelText: (typeof t?.label === "object" ? t.label?.text : t?.label) || "",
    labelExtract: l.extract,
    labelPrompt: l.prompt,
    labelCite: l.cite,
  };
}

export const tileRowDef = {
  type: "tile_row",
  label: "Tile Row",
  description: "Row or grid of metric tiles with value + label.",

  crystallizer: {
    extractionPhases(block) {
      const phases = new Set();
      for (const t of (block.tiles || [])) {
        const r = readTile(t);
        if (r.extract) phases.add("llm");
        if (r.formula) phases.add("structured");
        if (t.value?.derive) phases.add("deterministic");
      }
      if (block.derive) phases.delete("llm");
      if (phases.size === 0) phases.add("llm");
      return [...phases];
    },

    buildDataSchema(block, opts) {
      const hints = opts.hints || [];
      const ownedKeys = new Set(hints.map(h => h.key).filter(Boolean));
      const props = {};
      for (const t of (block.tiles || [])) {
        const r = readTile(t);
        if (r.extract) {
          if (ownedKeys.size > 0 && !ownedKeys.has(r.extract)) continue;
          props[r.extract] = { type: ["string", "number", "null"], description: r.prompt || r.labelText || r.extract };
          if (r.cite) props[r.extract + "__evidence"] = nullable("string", EVIDENCE_DESC);
        }
        if (r.formula) {
          for (const v of (r.formula.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [])) {
            if (ownedKeys.size > 0 && !ownedKeys.has(v)) continue;
            if (!props[v]) props[v] = nullable("number", `Numeric input for ${r.labelText} formula`);
          }
        }
        if (r.labelExtract) {
          if (ownedKeys.size > 0 && !ownedKeys.has(r.labelExtract)) continue;
          props[r.labelExtract] = nullable("string", r.labelPrompt || `Label text for tile ${r.extract || r.labelExtract}`);
          if (r.labelCite) props[r.labelExtract + "__evidence"] = nullable("string", EVIDENCE_DESC);
        }
      }
      return { type: "object", properties: props, required: Object.keys(props), additionalProperties: false };
    },

    buildShapeDescription(block) {
      const tks = (block.tiles || []).map(t => {
        const r = readTile(t);
        if (r.extract) return r.extract;
        if (r.formula) return (r.formula.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || []);
        return r.labelText.toLowerCase().replace(/\s+/g, "_");
      }).flat().filter(Boolean);
      return `{ ${tks.map(k => `"${k}": <value|null>`).join(", ")} }`;
    },

    isBlockLevelTarget() { return false; },
    skipFromPinSchema() { return false; },
    isCodeDerived(block) {
      return !!(block.derive && (block.tiles || []).every(t => t.value?.derive));
    },
    collectHints() { return []; },
    buildPromptSupplement() { return ""; },
    postProcess(data) { return data; },
  },

  renderer: null, // wired by ui/src/blockRegistryInit.js

  editor: {
    icon: "grid-3x3",
    category: "data-visualization",
    defaultTemplate: {
      type: "tile_row",
      tiles: [
        { value: { extract: "metric_a", format: "num_1dp" }, label: { text: "Metric A" } },
        { value: { extract: "metric_b", format: "pct_1dp" }, label: { text: "Metric B" } },
        { value: { extract: "metric_c", format: "currency_m" }, label: { text: "Metric C" } },
      ],
    },
    configFields: [
      { name: "cols", type: "number", label: "Grid columns (0=single row)", defaultValue: 0 },
      { name: "background", type: "color", label: "Row background" },
      { name: "tile_background", type: "color", label: "Tile background" },
      { name: "proportion", type: "number", label: "Proportion weight", step: 0.1 },
      { name: "tiles", type: "array", label: "Tiles",
        itemSchema: { fields: [
          { name: "value", type: "object", label: "Value",
            fields: [
              { name: "extract", type: "string", label: "Extract key" },
              { name: "format", type: "select", label: "Format", options: ["currency", "currency_m", "currency_gbp", "currency_gbp_m", "pct_1dp", "num_1dp", "num_0dp", ""], defaultValue: "num_1dp" },
              { name: "suffix", type: "string", label: "Suffix (e.g. x)" },
            ]
          },
          { name: "label", type: "object", label: "Label",
            fields: [
              { name: "text", type: "string", label: "Label text" },
            ]
          },
        ]}
      },
    ],
  },
};
